import { Add as AddIcon } from '@mui/icons-material';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Chip,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Container, DataGrid } from '@/components/common';
import { useGroupList } from '@/hooks/groups.hook';
import { LIST_VIEW_SESSION_KEYS } from '@/util/listViewSessionKeys';
import { useListViewSessionPersistence } from '@/util/useListViewSessionPersistence';
import { AssessPlatformCell, DateCell, GroupActionsCell, GroupCourseCell, CompletionRequirementsCell, DevelopmentCell } from '../components';

const GROUPS_DEFAULT_PAGINATION = { page: 0, pageSize: 10 };
const GROUPS_DEFAULT_FILTERS = {
  searchType: 'all',
  searchKeyword: '',
  queryOptions: { search: '' },
};

/**
 * 그룹 관리 페이지 컴포넌트
 * 
 * 그룹 목록을 조회하고 관리하는 페이지입니다.
 * 
 * 데이터 구조:
 * - groupId (int) 그룹ID
 * - groupName (string) 고객사명
 * - groupCourse (string) 그룹명
 * - groupCourseCount (int) 차수
 * - groupStartDate (datetime) 시작일
 * - groupDueDate (datetime) 종료일
 * - groupAssessId (int) 평가ID
 * - groupStatus (int) 교육 진행 상태 (대기 0, 진행 1, 종료 2)
 * - groupCompletePercentage (int) 완료율
 * - completionRequirements (array) 수료 조건 (ASSIGNMENT, DIAGNOSTIC, VIDEO 등)
 * - aiCodeReviewEnabled (boolean) AI 코드 리뷰 사용 여부
 */
const GroupsPage = () => {
  const navigate = useNavigate();

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetListSession,
  } = useListViewSessionPersistence(
    LIST_VIEW_SESSION_KEYS.groups,
    GROUPS_DEFAULT_PAGINATION,
    GROUPS_DEFAULT_FILTERS
  );

  const searchType = filters.searchType ?? 'all';
  const searchKeyword = filters.searchKeyword ?? '';
  const queryOptions = filters.queryOptions ?? GROUPS_DEFAULT_FILTERS.queryOptions;

  const setSearchType = useCallback((v) => {
    setFilters((f) => ({ ...f, searchType: v }));
  }, [setFilters]);
  const setSearchKeyword = useCallback((v) => {
    setFilters((f) => ({ ...f, searchKeyword: v }));
  }, [setFilters]);

  const { data, isLoading } = useGroupList({
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
    search: queryOptions.search,
  });

  const total = data?.total ?? 0;
  const rowData = data?.list ?? [];

  const [rowCountState, setRowCountState] = useState(0);
  useEffect(() => {
    if (!isLoading) setRowCountState(total);
  }, [total, isLoading]);

  /**
   * 검색 실행 핸들러
   */
  const handleSearch = useCallback(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setFilters((f) => ({
      ...f,
      queryOptions: { search: searchKeyword },
    }));
  }, [searchKeyword, setPaginationModel, setFilters]);

  /**
   * 검색 초기화 핸들러
   */
  const handleReset = useCallback(() => {
    resetListSession();
  }, [resetListSession]);

  /**
   * 행 클릭 핸들러
   */
  const handleRowClick = useCallback((params) => {
    navigate(`/groups/detail/${params.row.groupId}`);
  }, [navigate]);

  // 컬럼 정의 (메모이제이션하여 불필요한 리렌더링 방지)
  const columns = useMemo(() => [
    {
      field: 'groupId',
      headerName: '그룹ID',
      flex: 0.5,
      sortable: false,
      minWidth: 100,
      type: 'number',
    },
    {
      field: 'groupName',
      headerName: '고객사명',
      flex: 1,
      sortable: false,
      minWidth: 150,
    },
    {
      field: 'groupCourse',
      headerName: '그룹명',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => <GroupCourseCell row={params.row} />,
    },
    {
      field: 'groupStartDate',
      headerName: '시작일',
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <DateCell row={params.row} dateField="groupStartDate" />,
    },
    {
      field: 'groupDueDate',
      headerName: '종료일',
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <DateCell row={params.row} dateField="groupDueDate" />,
    },
    {
      field: 'groupAssessId',
      headerName: '역량평가 플랫폼 사용가능 여부',
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => <AssessPlatformCell row={params.row} />,
    },
    {
      field: 'aiCodeReviewEnabled',
      headerName: '프로젝트 개발훈련 사용가능 여부',
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <DevelopmentCell row={params.row} />,
    },
    {
      field: 'completionRequirements',
      headerName: '수료 조건',
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => <CompletionRequirementsCell row={params.row} />,
    },
    {
      field: 'actions',
      headerName: '사용자 관리',
      flex: 1.6,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <GroupActionsCell row={params.row} />,
    }
  ], []);

  return (
    <Container title='그룹 관리'>
      {/* 1. 상단 필터 및 버튼 영역 */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        {/* 검색 필터 그룹 */}
        <Stack direction="row" gap={1} alignItems="center">
          <FormControl size='small' sx={{ width: 120 }}>
            <InputLabel>검색 조건</InputLabel>
            <Select
              value={searchType}
              label="검색 조건"
              onChange={(e) => setSearchType(e.target.value)}
            >
              <MenuItem value='all'>전체</MenuItem>
              <MenuItem value='groupName'>고객사명</MenuItem>
              <MenuItem value='groupCourse'>그룹명</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size='small'
            placeholder='검색어 입력'
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ width: 200 }}
          />

          <Button variant='contained' onClick={handleSearch} sx={{ height: 40 }}>검색</Button>
          <Button variant='outlined' onClick={handleReset} sx={{ height: 40 }}>초기화</Button>
        </Stack>

        {/* 우측 액션 버튼 그룹 */}
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/groups/new')}
          >
            그룹 등록
          </Button>
        </Stack>
      </Stack>

      {/* 2. 데이터 그리드 */}
      <DataGrid
        rows={rowData}
        columns={columns}
        loading={isLoading}
        rowCount={rowCountState}
        height={'auto'}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row) => row.groupId}
        onRowClick={handleRowClick}
        onCellClick={(params, event) => {
          if (params.field === 'actions') {
            event.stopPropagation();
          }
        }}
      />
    </Container>
  );
};

export default GroupsPage;